<template>
  <div
    ref="tableRef"
    class="table"
  >
    <TableAction
      v-model:searchField="sourcesStore.sourcesSearchField"
      :filter-options="filterOptions"
      :filter-tag-options="filterTagOptions"
      :active-filter-count="activeFilterCount"
      :column-list="columnList"
      @move-column="args => updateColumnList(args)"
      @reset-filter="resetFilter"
      @add-filter-tag="args => addFilterTag(args)"
      @get-filter-tags="args => getFilterTags(args)"
      @get-filter-fields="getFilterFields"
      @update-filter="args => updateFilter(args)"
      @create-feed="tableStore.createFeedModalIsActive = true"
      @get-data="sourcesStore.pageNumber = 1; debounceGetData()"
    />
    <div
      ref="tableListRef"
      class="table__scroll scroll"
    >
      <TableHeader
        v-model:checkedAllRows="checkedAllRows"
        :table-width="tableWidth"
        :table-ref="tableRef"
        :current-sorting-column="sourcesStore.activeSortingColumn"
        :current-sorting-order="sourcesStore.activeSortingOrder"
        :column-list="columnList"
        @update-column-list="args => updateColumnList(args)"
        @change-sorting-column="val => changeSortingColumn(val)"
        @get-filtered-data="sourcesStore.pageNumber = 1; getData()"
        @active-cells-in-col="headerCellIndex => activeCellsInCol(headerCellIndex)"
      />
      <table
        v-if="isLoading"
        class="skeleton"
      >
        <tr
          v-for="i in 10"
          :key="i"
        >
          <td class="skeleton-td" />
        </tr>
      </table>
      <TableList
        v-else-if="tableStore.filteredListData.length"
        :table-width="tableWidth"
        :column-list="columnList"
        :table-data="tableStore.filteredListData"
        @edit-row="args => editSource(args)"
        @update-row="args => updateSource(args)"
        @change-feed-url="feed => changeFeedUrl(feed)"
      />
      <div
        v-else
        class="empty"
        :style="{width: tableWidth + 'px'}"
      >
        <div
          v-if="wasFirstRequest"
        >
          По вашему запросу ничего не найдено
        </div>
      </div>
    </div>

    <TableFooter
      v-model:take="sourcesStore.pageLimit"
      v-model:currentPage="sourcesStore.pageNumber"
      :quantity-lines="quantityLines"
      @update-source-list="getData"
    />

    <TableScroll :table-ref="tableListRef" />

    <CreateFeed
      v-if="tableStore.createFeedModalIsActive"
      key="CreateFeed"
      :tags="tags.results"
      @added="getData"
      @close-feed-modal="closeFeedModal"
    />
    <EditFeed
      v-if="isShowSourceEdit"
      key="EditFeed"
      title="Редактирования источника"
      :source="activeEditSource"
      @added="getData"
      @close-feed-modal="isShowSourceEdit = false"
    />
    <PopupConfirm
      key="removeFeedAlert"
      v-model:isActive="removeFeedAlert"
      message="Вы действительно хотите удалить выбранные источники?"
      max-width="378px"
      @cancel="removeFeedAlert = false"
    />
    <PopupConfirm
      key="unsavedChangesAlert"
      v-model:isActive="unsavedChangesAlert"
      message="Изменения не сохранятся после перехода на другую страницу"
      max-width="378px"
      @cancel="cancelLeave"
      @confirm="confirmLeave"
    />
    <SidebarTag
      v-if="tableStore.createTagModalIsActive"
      key="createTagModal"
      v-click-outside="closeTagModal"
    />
    <MultiTagEdit
      v-if="isShowMultiTagEdit"
      :source-count="selectedRowCount"
      @update-tag-ids="args => updateMultiRowTag(args)"
      @close-tag-editor="isShowMultiTagEdit = false"
    />
    <TableBar
      v-if="tableStore.filteredListData.filter((item) => item.checked).length > 0"
      :selected-count="tableStore.filteredListData.filter((item) => item.checked).length"
      @open-tag-editor="isShowMultiTagEdit = true"
      @reset-selection="resetSelection"
      @update-rows="args => updateRows(args)"
      @delete-rows="deleteRows"
    />
  </div>
</template>

<script setup>
import {computed, ref, watch, onMounted} from "vue";
import { debounce } from "@/tools/tools.js";
import { useTableStore } from "@/stores/table.js";
import environment from "@/environment";
import TableHeader from "@/components/table/TableHeader.vue";
import TableList from "@/components/table/TableList.vue";
import TableFooter from "@/components/table/TableFooter.vue";
import TableAction from "@/components/table/TableAction.vue";
import CreateFeed from "@/components/table/CreateFeed.vue";
import PopupConfirm from "@/components/table/PopupConfirm.vue";
import axios from "axios";
import SidebarTag from "@/components/SidebarTag.vue";
import TableScroll from "@/components/table/TableScroll.vue";
import {languageOptions, stateOptions, statusOptions, tableDataHeaders, typeOptions} from "@/exampleData/tableData";
import {useSources} from "@/stores/sources-store";
import TableBar from "@/components/table/TableBar.vue";
import MultiTagEdit from "@/components/table/MultiTagEdit.vue";
import EditFeed from "@/components/table/EditFeed.vue";
import { sortColumnsByOrder } from "@/composables/tableFunctions.js";

const tableStore = useTableStore();
const sourcesStore = useSources();

const tableRef = ref(null);
const tableListRef = ref(null);
const quantityLines = [10, 25, 50];

// Колонки. Перемещение, отключение.

const columnList = ref(tableDataHeaders);

const updateColumnList = (newColumnList) => {
  localStorage.setItem("tableColumnOrder", JSON.stringify(newColumnList.map((item) => item.name)));
  columnList.value = newColumnList;
};

// Количество активных фильтров

const activeFilterCount = computed(() => {
  // 1) считаем включённые опции во всех секциях
  const sectionsCount = filterOptions.value.reduce((sum, section) => {
    return sum + section.options.filter(o => o.value).length;
  }, 0);

  // 2) добавляем число выбранных тегов
  const tagsCount = filterTagOptions.value.selected.length;

  return sectionsCount + tagsCount;
});
// Редактирование источника

const isShowSourceEdit = ref(false);
const activeEditSource = ref(null);

const editSource = (source) => {
  activeEditSource.value = source;
  isShowSourceEdit.value = true;
};

const updateSource = async (source) => {
  await sourcesStore.updateSource(source.id, {[source.name]: source.value});
};

// Групповая замена тегов.

const isShowMultiTagEdit = ref(false);

const selectedRowCount = computed(() => tableStore.filteredListData.filter((item) => item.checked).length);

const updateMultiRowTag = async (tag_ids) => {
  for (const item of tableStore.filteredListData) {
    if (item.checked) {
      await sourcesStore.updateSourceTag(item.id, [...tag_ids, ...item.tag_ids]);
    }
  }
  isShowMultiTagEdit.value = false;
};

// tablebar functions

const resetSelection = () => {
  tableStore.filteredListData.map((item) => item.checked = false);
};

const deleteRows = async () => {
  for (const item of tableStore.filteredListData) {
    if (item.checked) {
      await sourcesStore.deleteSource(item.id);
    }
  }

  resetSelection();
};

const updateRows = async (type) => {
  if (type === "disable") {
    for (const item of tableStore.filteredListData) {
      if (item.checked) {
        await sourcesStore.updateSourceStatus(item.id, false);
      }
    }
  } else if (type === "archive") {
    for (const item of tableStore.filteredListData) {
      if (item.checked) {
        await sourcesStore.updateSourceArchive(item.id, true);
      }
    }
  }

  resetSelection();
};

// filter update

const getFilterFields = async (data) => {
  const { index, query } = data;
  try {
    const fieldName = filterOptions.value[index].id === "languages" ? "language" : filterOptions.value[index].id;
    const result = await sourcesStore.getFilterFields(fieldName, query);
    filterOptions.value[index].options = result.map((item) => { return {id: item, name: item, value: false}; });
  } catch (e) {
    console.log(e);
  }
};

const addFilterTag = (newTag) => {
  const isExistValue = filterTagOptions.value.selected.find((tag) => tag.id === newTag.id);

  if (isExistValue) {
    filterTagOptions.value.selected.splice(filterTagOptions.value.selected.indexOf(isExistValue), 1);
  } else {
    filterTagOptions.value.selected.push(newTag);
  }

  updateFilter();
};

const getFilterTags = async (query) => {
  filterTagOptions.value.query = query;
  filterTagOptions.value.options = await sourcesStore.getTags(query);
};

const resetFilter = async () => {
  filterOptions.value = JSON.parse(JSON.stringify(filterOptionsDefault));
  filterTagOptions.value.selected = [];
  await getData();
};

const updateFilter = () => {
  debounceGetData();
};

const filterOptions = ref([
  { id: "statuses",   label: "Статус",
    isOpen: false, useSearchbar: false, query: "", options: statusOptions },
  { id: "is_enabled",    label: "Состояние",
    isOpen: false, useSearchbar: false, query: "", options: stateOptions },
  { id: "types",     label: "Тип",
    isOpen: false, useSearchbar: false, query: "", options: typeOptions },
  { id: "languages", label: "Язык",
    isOpen: false, useSearchbar: false,  query: "", options: languageOptions },
]);

const filterTagOptions = ref({ id: "tag",      label: "Теги",
  isOpen: true, useSearchbar: true,  query: "", selected: [], options: {}});

const filterOptionsDefault = JSON.parse(JSON.stringify(filterOptions.value));



const changeSortingColumn = async (value) => {
  sourcesStore.activeSortingColumn = value.column;
  sourcesStore.activeSortingOrder = value.order;
  await getData();
};

const isLoading = ref (false);
let checkedAllRows = ref(false);
let wasFirstRequest = ref(false);
let removeFeedAlert = ref(false);
let unsavedChangesAlert = ref(false);
const tags = ref({});
const readyToCloseTagModal = ref(false);

let feedChecked = computed(() => {
  return tableStore.filteredListData.filter((feed) => feed.checked === true);
});

const tableWidth = computed(() => {
  let width = 0;
  if (tableStore.headersData) {
    for (const [, value] of Object.entries(tableStore.headersData)) {
      if (!value.hide) width += parseFloat(value.width);
    }
    return width;
  }
  return null;
});

const getData = async () => {
  isLoading.value = true;
  try {
    tableStore.requestIsExecution = true;

    const data = await sourcesStore.getSources(
        filterTagOptions.value,
        filterOptions.value
    );

    const pagination = {
      TotalCount: data.total_items,
      PageSize: sourcesStore.pageLimit,
      CurrentPage: data.current_page,
      TotalPages: Math.ceil(data.total_items / sourcesStore.pageLimit),
      HasNext: data.next_page,
      HasPrevious: data.previous_page
    };

    if (pagination) {
      tableStore.pagination = pagination;
    }

    const parsedData = JSON.parse(JSON.stringify(data.results));

    tableStore.listData = tableStore.filteredListData = parsedData;

  } catch (err) {
    console.error(err);
  } finally {
    tableStore.requestIsExecution = false;
    isLoading.value = false;
  }
};

const changeFeedUrl = async (feed) => {
  try {
    const url = `${environment.appConfig.baseApiUrl}${environment.changeFeedUrl}/${feed.id}`;
    await axios.put(url, feed.feedUrl);
  } catch(err) {
    console.error(err);
  }
};

const removeFeedPopup = async () => {
  if (feedChecked?.value?.length) removeFeedAlert.value = true; // Предупреждение перед удалением
};


const debounceGetData = debounce(getData, 500);

const activeCellsInCol = (headerCellIndex) => {
  tableStore.activeCells.clear();

  tableStore.filteredListData.forEach((row, index) => {
    tableStore.activeCells.add(`${index}-${headerCellIndex}`);
  });
};

const confirmLeave = () => {
  tableStore.wasUpdatedCells.clear();
  window.dispatchEvent(new Event("confirmLeave")); // Отправка события об подтверждении выхода
  window.hasUnsavedChanges = false;
  unsavedChangesAlert.value = false;
};

const cancelLeave = () => {
  unsavedChangesAlert.value = false;
};

const closeTagModal = () => {
  if (readyToCloseTagModal.value) {
    tableStore.createTagModalIsActive = false;
    readyToCloseTagModal.value = false;
  }
};

const closeFeedModal = () => {
  if (readyToCloseTagModal.value) {
    tableStore.createFeedModalIsActive = false;
    readyToCloseTagModal.value = false;
  }
};

watch(() => [tableStore.createTagModalIsActive, tableStore.createFeedModalIsActive], (val) => {
  if (val) {
    readyToCloseTagModal.value = false;
    setTimeout(() => {
      readyToCloseTagModal.value = true;
    }, 500);
  } else {
    readyToCloseTagModal.value = false; // сброс при закрытии
  }
});

watch(checkedAllRows, (newValue) => {
  tableStore.filteredListData.forEach(el => {
    el.checked = newValue;
  });
});

onMounted( async () => {
  const stored = JSON.parse(localStorage.getItem("tableColumnOrder")) || [];
  if (stored.length > 0) {
    columnList.value = sortColumnsByOrder(stored, columnList.value);
  }

  await getData();
  wasFirstRequest.value = true;
});
</script>

<style lang="scss" scoped>
.table {
  display: flex;
  flex-direction: column;

  border-radius: 8px;
  padding-bottom: 5px;

  &__scroll {
    overflow: auto;
    border-radius: 8px 8px 0 0;
    position: relative;
  }
}

.empty {
  min-height: 640px;
  background: #fff;
  padding: 20px;
}

.skeleton {
  width: 100%;
  height: calc(100vh - 279px);
  &-td {
    background: linear-gradient(90deg, #ffffff 25%, #f7f8fd 50%, #ffffff 75%);
    background-size: 200% 100%;
    min-height: 20px;
    margin: 0;
    animation: skeleton-loading 1.5s infinite;
  }
}

@keyframes skeleton-loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s cubic-bezier(.4,0,.2,1), transform 0.25s cubic-bezier(.4,0,.2,1);
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: translateX(40px);
}
.modal-fade-enter-to,
.modal-fade-leave-from {
  opacity: 1;
  transform: translateX(0);
}
</style>